import React, { useState } from 'react';
import styles from "./Home.module.css";
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react'
import { debounce } from 'lodash'
import { useRef } from 'react'
import Header from './Header';
import { colors } from '../../data/colorsDict';

const searchColors = [
  { name: "Web Dev", dot: "#22c55e", line: "#d1fae5", tagBg: "#dcfce7", tagColor: "#15803d", width: "70%" },
  { name: "REST API", dot: "#8b5cf6", line: "#ede9fe", tagBg: "#ede9fe", tagColor: "#7c3aed", width: "55%" },
  { name: "Data Science", dot: "#0492C2", line: "#bae6fd", tagBg: "#dbeafe", tagColor: "#1d4ed8", width: "80%" },
  { name: "AI", dot: "#ef4444", line: "#fee2e2", tagBg: "#fee2e2", tagColor: "#dc2626", width: "60%" },
];

const sortOptions = [
  { label: "Title A→Z", active: true, arrow: "✓" },
  { label: "Created date", active: false, arrow: "↕" },
  { label: "Modified date", active: false, arrow: "↕" },
];

const filterTags = [
  { label: "Web Dev", active: true },
  { label: "AI", active: false },
  { label: "REST API", active: true },
  { label: "Data Science", active: false },
];

const steps = [
  {
    num: "01",
    icon: "✍️",
    title: "Create your note",
    desc: "Write a title and your content. No formatting required — just start typing and let your thoughts flow freely."
  },
  {
    num: "02",
    icon: "🎨",
    title: "Tag & color it",
    desc: "Pick a color border and add tags to categorize your note exactly the way you think. Make it visually yours."
  },
  {
    num: "03",
    icon: "🔍",
    title: "Find it instantly",
    desc: "Search by title or content, filter by tag or color, and sort however suits you. Zero friction retrieval."
  }
];

const Home = () => {
  const [showMenu,setShowMenu] = useState(false)
  const [scrolled,setScrolled] = useState(false)
  const box = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    const container = box.current
    const handleScroll = debounce(() => {
      if (box.current){
          setScrolled(box.current.scrollTop>0)
      }
    }, 100);

    box.current?.addEventListener('scroll', handleScroll)
    return () => {
      container?.removeEventListener('scroll', handleScroll)
      handleScroll.cancel()
    };
  }, [])

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        const wrapper = document.querySelector(`.${styles.wrapper}`)
        if (el && wrapper) {
            const y = el.offsetTop - 56
            wrapper.scrollTo({ top: y, behavior: "smooth" })
        }
    }

  return (
    <div className = {`${styles.wrapper} ${showMenu ? styles.openMenu : ""}`} ref = {box}>
      <Header
        scrolled = {scrolled}
        scrollTo = {scrollTo}
        showMenu = {showMenu}
        setShowMenu = {setShowMenu}
      />
      <div className = {styles.components}>
        <div 
          className = {styles.descriptionWrapper}
          id = "about"
        >
          <div className = {styles.descriptionBox} style={{ backgroundImage: `url('/assets/heroImage.png')` }}>
            <div className = {styles.info}>
              <div className ={styles.eyeBrow}>✦ &nbsp;Your ideas deserve better</div>
              <h1>Notes that stay <br/><em>perfectly</em><br/>organized.</h1>
              <p>NotePro brings order to your notes with color-coded organization, custom tags, and a search that works the way your brain does.</p>
              <div className = {styles.heroBtns}>
                <button type = "button" className = {styles.startBtn} onClick = {()=>navigate("/signup")}>Start for free →</button>
                <button type = "button" className = {styles.workBtn} onClick = {()=>scrollTo("works")}>See how it works</button>
              </div>
              <div className = {styles.props}>
                <div className = {styles.prop}>Free to use</div>
                <div className = {styles.prop}>No limits</div>
                <div className = {styles.prop}>Private & secure</div>
              </div>
            </div>
            <div className = {styles.imageArea}>
              <img
                src = "assets/mobileHeroImage.png"
                alt="Mobile Header"
                loading="lazy"
              />
            </div>
          </div>
        </div>
        <div className = {styles.howSection} id = "works">
          <div className = {styles.howHeader}>
            <div>
              <div className = {styles.sectionEyebrow}>How it works</div>
              <h2>Three steps to never<br/>lose a note again.</h2>
            </div>
            <p>No learning curve. Just open the app, write your note, and find it whenever you need it.</p>
          </div>
          <div className={styles.steps}>
            {steps.map((step, i) => (
              <div key={i} className={styles.step}>
                <div className={styles.stepNum}>{step.num}</div>
                <div className={styles.stepIcon}>{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
          <div className={styles.mSteps}>
            {steps.map((step, i) => (
              <div key={i} className={styles.mStep}>
                <div className={styles.mStepLeft}>
                  <div className={styles.mStepNum}>{step.num}</div>
                  <div className={styles.mStepIcon}>{step.icon}</div>
                </div>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className = {styles.featuresBox} id="features">
          <div className = {styles.featuresInner}>
            <div className = {styles.sectionEyebrow}>Features</div>
            <div className = {styles.sectionHeading}>Built around the way<br/>you <em>actually</em> think.</div>
            <div className = {styles.sectionSub}>Every note has structure. Every structure has a purpose. Here's what makes NotePro work.</div>
            <div className = {styles.compontentsLabel}>Note components</div>
            <div className = {styles.componentsBox}>
              <div className = {styles.componentCard}>
                <div className = {styles.componentNum}>01</div>
                <div className = {styles.componentIcon}>📝</div>
                <div className ={ `${styles.componentBadge} ${styles.req}`}>Required</div>
                <div className = {styles.componentTitle}>Title</div>
                <div className = {styles.componentDesc}>A concise headline for every note — making it instantly recognizable and easy to scan.</div>
              </div>
              <div className = {styles.componentCard}>
                <div className = {styles.componentNum}>02</div>
                <div className = {styles.componentIcon}>📄</div>
                <div className ={ `${styles.componentBadge} ${styles.req}`}>Required</div>
                <div className = {styles.componentTitle}>Content</div>
                <div className = {styles.componentDesc}>The core of your note. Capture detailed ideas and records preserved for future access.</div>
              </div>
              <div className = {styles.componentCard}>
                <div className = {styles.componentNum}>03</div>
                <div className = {styles.componentIcon}>🎨</div>
                <div className ={ `${styles.componentBadge} ${styles.req}`}>Required</div>
                <div className = {styles.componentTitle}>Color</div>
                <div className = {styles.componentDesc}>Every note gets a color. Visual associations that make categorizing and sorting effortless.</div>
              </div>
              <div className = {styles.componentCard}>
                <div className = {styles.componentNum}>04</div>
                <div className = {styles.componentIcon}>🏷️</div>
                <div className ={ `${styles.componentBadge} ${styles.opt}`}>Optional</div>
                <div className = {styles.componentTitle}>Tags</div>
                <div className = {styles.componentDesc}>Add custom tags to any note. Group related ideas and filter your library with precision.</div>
              </div>
            </div>
            <div className={styles.orgLabel}>Organizing tools</div>
            <div className={styles.orgBento}>
              <div className={styles.orgWideCard}>
                <div className={styles.orgWideCardBg}></div>
                <div>
                  <div className={styles.orgIconWrap}>🔍</div>
                  <div className={styles.orgTitle}>Search</div>
                  <div className={styles.orgDesc}>
                    Find any note in milliseconds. NotePro searches across both titles and full content — so nothing ever gets buried.
                  </div>
                </div>
                <div>
                  <div className={styles.orgMock}>
                    {searchColors.map((c, i) => (
                      <div key={i} className={styles.orgMockRow}>
                        <div className={styles.orgMockDot} style={{ background: c.dot }}></div>
                        <div className={styles.orgMockLine} style={{ background: c.line, width: c.width }}></div>
                        <div className={styles.orgMockTag} style={{ background: c.tagBg, color: c.tagColor }}>
                          {c.name}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={styles.orgTags}>
                    <span className={styles.orgTag}>By title</span>
                    <span className={styles.orgTag}>By content</span>
                  </div>
                </div>
              </div>
              <div className={styles.orgTallCard}>
                <div className={styles.orgIconWrap}>↕️</div>
                <div className={styles.orgTitle}>Sort</div>
                <div className={styles.orgDesc}>
                  Order notes exactly how you think — alphabetically or by when a note was created or last modified.
                </div>
                <div className={styles.sortMock}>
                  {sortOptions.map((opt, i) => (
                    <div key={i} className={`${styles.sortRow} ${opt.active ? styles.active : ""}`}>
                      <div className={styles.sortDot}></div>
                      <div className={styles.sortLabel}>{opt.label}</div>
                      <div className={styles.sortArrow}>{opt.arrow}</div>
                    </div>
                  ))}
                </div>
                <div className={styles.orgTags}>
                  <span className={styles.orgTag}>By title</span>
                  <span className={styles.orgTag}>Created</span>
                  <span className={styles.orgTag}>Modified</span>
                </div>
              </div>
              <div className={styles.orgTallCard}>
                <div className={styles.orgIconWrap}>🎛️</div>
                <div className={styles.orgTitle}>Filter</div>
                <div className={styles.orgDesc}>
                  Narrow your notes visually. Filter by color to find a category instantly, or by tag to surface related notes.
                </div>
                <div className={styles.filterMock}>
                  <div className={styles.filterLabel}>Color</div>
                  <div className={styles.filterSwatches}>
                    {colors.map((s, i) => (
                      <div key={i} className={`${styles.filterSwatch} ${i === 0 ? styles.active : ""}`} style={{ background: s.code }}></div>
                    ))}
                  </div>
                  <div className={styles.filterLabel}>Tags</div>
                  <div className={styles.filterTagsRow}>
                    {filterTags.map((t, i) => (
                      <div key={i} className={`${styles.filterTag} ${t.active ? styles.active : ""}`}>
                        {t.label}
                      </div>
                    ))}
                  </div>
                </div>
                <div className={styles.orgTags}>
                  <span className={styles.orgTag}>By color</span>
                  <span className={styles.orgTag}>By tag</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer>
        <p>© {new Date().getFullYear()} NotePro. All rights reserved.</p>
      </footer>
    </div>
  )
}

export default Home